import { useState, useRef, useEffect, useCallback, useId } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import {
  VALID_VARIANT_STRINGS,
  CHALLENGE_COLORS,
  DEFAULT_CHALLENGE_COLOR,
  MATCH_TYPES,
  DEFAULT_MATCH_TYPE,
  canJoinMatchType,
  resolveMatchType,
  GAME_MODES,
  DEFAULT_GAME_MODE,
  openingModesFor,
  resolveOpeningMode,
  variantHasBallot,
  DEFAULT_OPENING_MODE,
  MATCH_LEGS_OPTIONS,
  defaultLegsFor,
  isDrawnOpening,
  isUnratableSingleLeg,
  CHALLENGE_MINUTES,
  CHALLENGE_INCREMENTS,
  buildTimeControlKey,
  categoryForClock,
  parseTimeControlKey,
} from '@draughts/shared';
import type { ChallengeColor, ChallengeSettings, GameMode, MatchLegs, MatchType, MatchTypeParticipant, OpeningMode, TimeControlKey } from '@draughts/shared';
import { usePreferencesStore } from '../../store/preferences-store';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { CADENCE_ICONS } from '../../utils/cadence-icons';
import { colorLeading, gameTypeLeading, gameModeLeading, openingLeading, legsLeading } from '../../utils/challenge-icons';
import { colorLabel, gameModeLabel, matchTypeLabel, knownMatchTypeLabel } from '../../utils/challenge-labels';
import { openingOptionLabel } from '../../utils/opening-labels';
import {
  getLastVariant,
  setLastVariant,
  getLastChallengeTimeControl,
  setLastChallengeTimeControl,
  getLastChallengeColor,
  setLastChallengeColor,
  getLastChallengeMatchType,
  setLastChallengeMatchType,
  getLastChallengeGameMode,
  setLastChallengeGameMode,
  getLastChallengeOpening,
  setLastChallengeOpening,
  getLastChallengeLegs,
  setLastChallengeLegs,
} from '../../utils/last-used-play';
import { buildOpeningsPath } from '../../seo/routes';
import VariantSelect from '../profile/VariantSelect';
import ToggleSwitch from '../common/ToggleSwitch';

/**
 * The game-settings rows shared by the two ways of proposing a game: a direct
 * challenge to one player, and a public table anybody can sit at.
 *
 * Estratte dal vecchio modale della Sfida diretta perche' la "partita personalizzata"
 * offers exactly the same options with exactly the same rules — the ∞-is-casual
 * note, the ballot explainer, the "andata e ritorno" row and the default it starts
 * from. Two copies of this would drift within a release.
 *
 * The component owns the state and reports the assembled `ChallengeSettings`
 * upward; the parent decides what to do with them (send, publish, share).
 */

// Cadence is picked as two independent values — minutes and increment — rather
// than from a fixed preset list, so the two players can agree on any of the
// ~490 combinations. `buildTimeControlKey` turns the pair into the canonical
// `<category>-<min>+<inc>` key the server and the DB expect.
const INFINITE = 'inf';
const MINUTE_VALUES = [...CHALLENGE_MINUTES.map(String), INFINITE];
const INCREMENT_VALUES = CHALLENGE_INCREMENTS.map(String);

/** Split a cadence key back into the two dropdown values it was built from. */
function splitTimeControl(tc: string | undefined): { minutes: string; increment: string } {
  if (!tc || tc === 'none') return { minutes: INFINITE, increment: '0' };
  const parsed = parseTimeControlKey(tc);
  if (!parsed) return { minutes: '5', increment: '3' };
  return { minutes: String(parsed.minutes), increment: String(parsed.increment) };
}

export interface ChallengeOptionsFieldsProps {
  /** Called on mount and after every change with the assembled settings. */
  onChange: (settings: ChallengeSettings) => void;
  /** Pre-selected variant. Lowercase. */
  defaultVariant?: string;
  /** Pre-selected cadence preset. */
  defaultTimeControl?: string;
  /**
   * Custom starting position (FEN) to play from — set when the game is launched
   * from the board EDITOR. Locks the variant and hides the opening rows.
   */
  position?: string;
  /**
   * Force the game casual and hide the game-type selector. Used with `position`
   * (composed boards are never rated), and by a guest host, who can only ever
   * offer a friendly game.
   */
  lockRated?: boolean;
  /**
   * Cosa mostrare per spiegare PERCHE' il rating e' bloccato, quando c'e' qualcosa
   * da dire. Un nodo, non una stringa: al foglio della partita personalizzata serve
   * il riquadro d'invito con il bottone d'accesso, non una riga di testo. Il markup
   * lo decide chi passa la prop — qui si monta e basta.
   *
   * Vive QUI e non nell'`headerSlot` di chi ci ospita perche' quello slot puo' non
   * essere montato (il foglio lo mostra solo con piu' di una destinazione), e questo
   * avviso non deve poter sparire.
   */
  lockRatedNote?: ReactNode;
  /**
   * When set, every dropdown is seeded from the device-local "last used" store
   * and each change is persisted there, so reopening restores the last choice.
   * Off for the board-editor caller.
   */
  rememberSettings?: boolean;
  /**
   * Partita in presenza: entrambi i giocatori sono a QUESTO dispositivo.
   *
   * Toglie le due righe che li' non vogliono dire niente. Niente valida/amichevole:
   * non c'e' nulla da registrare, nessun punteggio da muovere. Niente colore: il
   * Bianco parte in basso e chi vuole cambiare lato gira la damiera in partita.
   * Apertura e andata/ritorno restano — servono anche, e soprattutto, dal vivo.
   */
  hotSeat?: boolean;
  /**
   * Partita contro il computer: locale come l'hot-seat, ma il colore si sceglie.
   *
   * Toglie le tre righe che contro l'AI non vogliono dire niente. Niente orologio
   * (la Sfida AI non ne ha mai avuto uno). Niente valida/amichevole: non c'è niente
   * da registrare. Niente andata e ritorno: `startReturnLeg` non scambia il colore
   * dell'umano, quindi il ritorno si rigiocherebbe dallo stesso lato — e la Sfida AI
   * non lo offre. Restano variante, apertura, colore e modalità.
   */
  vsComputer?: boolean;
  /**
   * L'avversario già scelto, quando ce n'è uno (Sfida diretta).
   *
   * Con un destinatario in carne e ossa il tipo partita non si chiede più in
   * astratto: «qualsiasi» è una politica della porta, e qui la porta è già chiusa
   * su una persona sola. Quindi la tendina a tre voci diventa un interruttore —
   * valutata o amichevole — e sparisce del tutto se quella persona non può muovere
   * un punteggio, che è lo stesso `canJoinMatchType` con cui il server rifiuterebbe
   * l'invio, detto dove la scelta si può ancora cambiare.
   */
  opponent?: MatchTypeParticipant;
  /**
   * Apre le tabelle di apertura DENTRO l'app invece che nel browser.
   *
   * Senza, il link resta quello che era: una URL vera in una scheda nuova — che in
   * PWA installata vuol dire uscire dall'app.
   */
  onOpenOpeningTables?: (variant: string) => void;
  /** Riga extra montata come primo figlio delle impostazioni (la scelta dell'avversario). */
  headerSlot?: ReactNode;
}

export default function ChallengeOptionsFields({
  onChange,
  defaultVariant = 'international',
  defaultTimeControl = 'blitz-5+3',
  position,
  lockRated = false,
  lockRatedNote,
  rememberSettings = false,
  hotSeat = false,
  vsComputer = false,
  opponent,
  onOpenOpeningTables,
  headerSlot,
}: ChallengeOptionsFieldsProps) {
  const { t, i18n } = useTranslation();
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);

  // Seeded once per mount. Without rememberSettings this reflects the passed
  // defaults (live hero selection / editor context); with it, each dropdown
  // restores from the device-local "last used" store, falling back to the
  // default. The challenge cadence uses its OWN store key so it can remember '∞'
  // (none) without ever feeding 'none' to the dashboard summary.
  const [variant, setVariant] = useState<string>(
    () => (rememberSettings ? getLastVariant() ?? defaultVariant : defaultVariant),
  );
  const [cadence, setCadence] = useState<{ minutes: string; increment: string }>(() =>
    splitTimeControl(rememberSettings ? getLastChallengeTimeControl() ?? defaultTimeControl : defaultTimeControl),
  );
  const [color, setColor] = useState<ChallengeColor>(
    () => (rememberSettings ? getLastChallengeColor() ?? DEFAULT_CHALLENGE_COLOR : DEFAULT_CHALLENGE_COLOR),
  );
  const [matchType, setMatchType] = useState<MatchType>(() => {
    if (lockRated) return 'casual';
    if (rememberSettings) {
      const m = getLastChallengeMatchType();
      if (m !== null) return m;
    }
    return DEFAULT_MATCH_TYPE;
  });
  const [gameMode, setGameMode] = useState<GameMode>(
    () => (rememberSettings ? getLastChallengeGameMode() ?? DEFAULT_GAME_MODE : DEFAULT_GAME_MODE),
  );
  // Raw choice: `opening` below is this value resolved against the variant, so
  // switching to a variant with no ballot does not lose the user's pick when they
  // switch back.
  const [rawOpening, setOpening] = useState<OpeningMode>(
    () => (rememberSettings ? getLastChallengeOpening() ?? DEFAULT_OPENING_MODE : DEFAULT_OPENING_MODE),
  );
  /*
   * Le manche: `null` = nessuna scelta esplicita, si segue il predefinito
   * dell'apertura (`defaultLegsFor`, risolto più sotto insieme a `opening`).
   *
   * Tenere la scelta separata dal predefinito è ciò che fa tornare da sé a «solo
   * andata» quando si passa alla mossa libera — dove il ritorno si può chiedere ma
   * non è assunto — senza nessun effetto che rincorra il menu dell'apertura. Una
   * scelta esplicita invece resta, da una parte e dall'altra del confine.
   *
   * La preferenza RICORDATA vale solo col sorteggio, che è dove è stata formata:
   * finché il ritorno esisteva solo lì, un «andata e ritorno» salvato non poteva
   * voler dire nulla sulla mossa libera, e non deve raddoppiarne le partite.
   */
  const [legsChoice, setLegsChoice] = useState<MatchLegs | null>(null);
  const rememberedLegs = useRef(rememberSettings ? getLastChallengeLegs() : null).current;

  /**
   * Gli aiuti «?»: il testo si apre NEL FLUSSO, sotto l'etichetta e sopra il menu.
   *
   * Prima era un tooltip in assoluto ancorato all'icona, che apriva SEMPRE verso l'alto
   * (`bottom: 100%`, mai misurato). Su mobile il foglio è a tutto schermo e il corpo è il
   * contenitore che scorre (`.play-online-body { overflow-y: auto }`): tutto ciò che saliva
   * sopra il suo bordo veniva RITAGLIATO, e l'aiuto dell'Apertura — che è la terza riga di
   * sette, non l'ultima come il commento del CSS dava per scontato — restava illeggibile.
   * In flusso non c'è più niente da misurare e niente che possa tagliarlo.
   *
   * IL REF STA SULLA RIGA, non sul bottone: la nota ora è FUORI dal bottone, e il gestore
   * qui sotto chiude su `mousedown`. Con un ref più stretto il nodo verrebbe smontato prima
   * che il `click` sul link «Vedi le tabelle di apertura» arrivi a destinazione — cioè il
   * link smetterebbe di funzionare.
   */
  const hintId = useId().replace(/[:]/g, '');
  const [modeHintOpen, setModeHintOpen] = useState(false);
  const modeHintRef = useRef<HTMLDivElement>(null);
  const closeModeHint = useCallback(() => setModeHintOpen(false), []);
  // Same contract for the opening explainer: the F.I.D. strength codes (1N…4B)
  // mean nothing without the legend, and the rating rule hangs off them.
  const [openingHintOpen, setOpeningHintOpen] = useState(false);
  const openingHintRef = useRef<HTMLDivElement>(null);
  const closeOpeningHint = useCallback(() => setOpeningHintOpen(false), []);

  useEffect(() => {
    if (!modeHintOpen) return;
    const onDocMouse = (e: MouseEvent) => {
      if (modeHintRef.current && !modeHintRef.current.contains(e.target as Node)) closeModeHint();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // I fogli che ospitano queste righe ascoltano Esc su `window`, che sul cammino di
      // bubbling viene DOPO `document`: senza fermarla qui, un Esc con l'aiuto aperto
      // chiuderebbe l'aiuto e il foglio insieme. Si chiude uno strato alla volta.
      e.stopPropagation();
      closeModeHint();
    };
    document.addEventListener('mousedown', onDocMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [modeHintOpen, closeModeHint]);

  useEffect(() => {
    if (!openingHintOpen) return;
    const onDocMouse = (e: MouseEvent) => {
      if (openingHintRef.current && !openingHintRef.current.contains(e.target as Node)) closeOpeningHint();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Vedi il gemello qui sopra: uno strato alla volta.
      e.stopPropagation();
      closeOpeningHint();
    };
    document.addEventListener('mousedown', onDocMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [openingHintOpen, closeOpeningHint]);

  const variantLabel = (v: string): string => variantShortLabel(t, v);

  // ── Cadence ────────────────────────────────────────────────────────────────
  // Contro il computer non c'è orologio: la riga sparisce e la cadenza vale 'none',
  // che è quello che ogni percorso della Sfida AI ha sempre passato.
  const isUntimed = vsComputer || cadence.minutes === INFINITE;
  // Untimed games carry no increment and are always casual (the server enforces
  // both), so those two controls are hidden rather than left in a dead state.
  const timeControl: TimeControlKey = isUntimed
    ? 'none'
    : buildTimeControlKey(Number(cadence.minutes), Number(cadence.increment));

  // ── Opening ────────────────────────────────────────────────────────────────
  // The F.I.D. ballot only exists for italian/english, so the four "sorteggiata
  // max …" entries are offered there alone. A ballot already picked on another
  // variant degrades to the equivalent random draw instead of silently becoming
  // a free game — same rule the server applies.
  const hasBallot = variantHasBallot(variant);
  const openingModes = openingModesFor(variant);
  const opening = resolveOpeningMode(rawOpening, variant);
  // Vedi `legsChoice`: la scelta esplicita, poi la preferenza ricordata (solo col
  // sorteggio), poi il predefinito dell'apertura.
  const legs: MatchLegs =
    legsChoice ?? (isDrawnOpening(opening) ? rememberedLegs : null) ?? defaultLegsFor(opening);

  // Above the F.I.D. single-game ceiling a drawn opening played once is always
  // casual (the server enforces it too): the draw favours one side and only the
  // return leg hands it to the other player. Treated like ∞ — the rated/casual
  // selector is hidden rather than left dead.
  const drawnSingleLeg = !position && isUnratableSingleLeg(opening, legs);

  // ── Report upward ──────────────────────────────────────────────────────────
  // Through a ref so a parent that passes an inline arrow does not re-fire this
  // effect on every render of its own.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // The same three forced downgrades the server applies in
  // `normalizeChallengeSettings`; mirrored here only so the parent's preview and
  // the wire payload agree with what the player is being shown.
  // Una sola condizione per le tre cose che dipendono dal rating.
  const hideRating = lockRated || hotSeat || vsComputer;
  /*
   * Un avversario gia' scelto CHIUDE la porta, e con la porta chiusa «qualsiasi»
   * non e' piu' un tipo di partita: il «chiunque» e' una persona sola e la si
   * conosce. Quindi la scelta a tre valori si accartoccia — un interruttore,
   * valutata o amichevole, acceso per difetto — e sparisce del tutto quando quella
   * persona non puo' muovere un punteggio, con la nota al posto del comando.
   *
   * Le altre tre destinazioni (tavolo pubblico, computer, in presenza) non hanno
   * un destinatario: per loro `opponent` e' assente e tutto resta la tendina di
   * prima, «Valutata/Amichevole» compreso — la' vuol dire ancora qualcosa.
   */
  const opponentIsGuest = !!opponent && !canJoinMatchType('rated', opponent);
  // La scelta GREZZA resta quella scelta (come rawOpening/opening): un «qualsiasi»
  // ricordato dal dispositivo vale «conta se può», che con un avversario noto è
  // esattamente `resolveMatchType`, e tornare su un iscritto ritrova la scelta di
  // prima invece di averla persa.
  const resolvedMatchType: MatchType = opponent ? resolveMatchType(matchType, opponent) : matchType;
  /*
   * ⚠️ `opponentIsGuest` sta in questo elenco e non e' una ridondanza del comando
   * nascosto. `resolveMatchType` NON declassa `'rated'`, di proposito: e' l'unico
   * tipo che la porta usa per rifiutare. Quindi senza questa condizione una
   * preferenza ricordata `'rated'` piu' un ospite come bersaglio spedirebbe
   * `'rated'`, il server risponderebbe `REGISTERED_ONLY` e `handleChallengePlayer`
   * quell'errore lo INGHIOTTE (solo `log.error`): un clic morto, senza una parola.
   *
   * Un avversario ospite e' un declassamento forzato della stessa famiglia di ∞ e
   * della pescata in partita singola, e sta nell'elenco con loro.
   */
  const effectiveMatchType: MatchType =
    hideRating || isUntimed || drawnSingleLeg || opponentIsGuest ? 'casual' : resolvedMatchType;
  const effectiveOpening = position ? 'free' : opening;
  // In presenza il colore non e' una scelta: il Bianco sta in basso e muove per primo.
  const effectiveColor: ChallengeColor = hotSeat ? 'white' : color;
  const effectiveLegs: MatchLegs = !position && !vsComputer ? legs : 1;

  useEffect(() => {
    onChangeRef.current({
      variant,
      timeControl,
      color: effectiveColor,
      matchType: effectiveMatchType,
      rated: effectiveMatchType !== 'casual',
      gameMode,
      opening: effectiveOpening,
      legs: effectiveLegs,
      ...(position ? { position } : {}),
    });
  }, [variant, timeControl, effectiveColor, effectiveMatchType, gameMode, effectiveOpening, effectiveLegs, position]);

  const setCadencePart = (part: 'minutes' | 'increment', value: string) => {
    const next = { ...cadence, [part]: value };
    setCadence(next);
    if (!rememberSettings) return;
    setLastChallengeTimeControl(
      next.minutes === INFINITE
        ? 'none'
        : buildTimeControlKey(Number(next.minutes), Number(next.increment)),
    );
  };

  // 'min' is the unit symbol for minutes — like the increment's 's', it reads the
  // same in all 7 supported locales, so it is not a translated string. ∞ carries
  // no unit: it means "no clock", not "infinite minutes".
  const minutesLabel = (v: string): string => (v === INFINITE ? '∞' : `${v} min`);
  // The leading glyph tells a numeric option which cadence category it lands in.
  // '∞' is already that glyph, so it gets none — the summary below spells it out.
  const minutesLeading = (v: string): ReactNode =>
    v === INFINITE ? null : CADENCE_ICONS[categoryForClock(Number(v), Number(cadence.increment))] ?? null;
  // 's' is the SI symbol for seconds — identical in all 7 supported locales,
  // so it is not a translated string.
  const incrementLabel = (v: string): string => `+${v} s`;

  /*
   * La scelta del tipo partita, da entrambi i comandi (tendina e interruttore).
   *
   * ⚠️ Nella Sfida diretta «valutata» si RICORDA come `'any'`, non come `'rated'`.
   * `'rated'` è il cartello «niente ospiti», e con un avversario già noto e iscritto
   * non è quello che si è scelto: si è scelto «che conti». Ricordarlo alla lettera
   * chiuderebbe la porta al prossimo TAVOLO PUBBLICO — la chiave del dispositivo è
   * UNA e la condividono tutte le destinazioni — tenendo fuori gli ospiti da un
   * tavolo che non li voleva tenere fuori. `'any'` è la stessa intenzione senza il
   * cartello, e davanti a un iscritto si rilegge «valutata».
   */
  const scegliTipo = (v: MatchType) => {
    setMatchType(v);
    if (rememberSettings) setLastChallengeMatchType(opponent && v === 'rated' ? 'any' : v);
  };

  // Colore e modalità: etichette condivise con la Sfida AI (utils/challenge-labels).
  const colorLabelFor = (c: string): string => colorLabel(t, c);
  const gameModeLabelFor = (m: string): string => gameModeLabel(t, m);

  const openingLabel = (o: string): string => openingOptionLabel(t, o as OpeningMode, variant);

  // MatchLegs is numeric but VariantSelect speaks strings, so the dropdown keeps
  // '1'/'2' and converts at the boundary.
  const LEGS_VALUES = MATCH_LEGS_OPTIONS.map(String);
  const legsLabel = (l: string): string =>
    l === '1' ? t('online.challenge.legsOne') : t('online.challenge.legsTwo');

  return (
    <div className="challenge-settings">
      {headerSlot}
      {/* Questa nota non parla di un'impostazione: parla della PERSONA, e il suo nome
          è l'ultima riga dell'intestazione qui sopra. Quindi si legge lì, non in fondo
          al modulo dove stava il comando «Valutata» che sostituisce: in mezzo a
          orologio, apertura e partite — a 360px, mezzo schermo sotto il nome — era una
          riga sull'avversario messa dove non si parla dell'avversario.

          Le tre guardie sono le stesse del comando, e servono a non raddoppiare un
          «sempre amichevole» che un'altra riga ha già detto: con ∞ parla la nota
          dell'orologio, con la pescata in partita singola quella sotto «Partite». Il
          prezzo è che quelle due stanno PIÙ IN BASSO di questa, quindi cambiando la
          cadenza la nota in testa se ne va e il modulo si alza di una riga: meglio
          questo che la stessa frase scritta due volte. */}
      {!hideRating && !isUntimed && !drawnSingleLeg && opponentIsGuest && (
        <p className="challenge-custom-note">{t('online.challenge.opponentGuestNoRated')}</p>
      )}
      {position && <p className="challenge-custom-note">{t('editor.challengeNote')}</p>}
      {lockRated && !position && lockRatedNote}
      <div className="challenge-setting">
        <label className="challenge-setting-label">{t('home.variant')}</label>
        {/* A custom position is variant-specific: lock the variant so the FEN can't be
            reinterpreted under a different board (the server parses it with this variant). */}
        {position ? (
          <div className="challenge-fixed-variant">
            {variantPill(variant).flag}
            <span>{variantLabel(variant)}</span>
          </div>
        ) : (
          <VariantSelect
            value={variant}
            onChange={(v) => { setVariant(v); if (rememberSettings) setLastVariant(v); }}
            variants={VALID_VARIANT_STRINGS}
            preferred={preferredVariant}
            label={variantLabel}
            dotColor={() => 'transparent'}
            renderLeading={(v) => variantPill(v).flag}
            ariaLabel={t('home.variant')}
          />
        )}
      </div>

      {/* Cadence: minutes × increment. Two dropdowns side by side so the
          pair reads as one control; the increment column collapses for ∞.
          Assente contro il computer: quella partita non ha orologio. */}
      {!vsComputer && (
        <div className={`challenge-cadence-row${isUntimed ? ' no-increment' : ''}`}>
          <div className="challenge-setting">
            <label className="challenge-setting-label">{t('online.challenge.time')}</label>
            <VariantSelect
              value={cadence.minutes}
              onChange={(m) => setCadencePart('minutes', m)}
              variants={MINUTE_VALUES}
              label={minutesLabel}
              dotColor={() => 'transparent'}
              renderLeading={minutesLeading}
              ariaLabel={t('online.challenge.time')}
            />
          </div>

          {!isUntimed && (
            <div className="challenge-setting">
              <label className="challenge-setting-label">{t('online.challenge.increment')}</label>
              <VariantSelect
                value={cadence.increment}
                onChange={(i) => setCadencePart('increment', i)}
                variants={INCREMENT_VALUES}
                label={incrementLabel}
                dotColor={() => 'transparent'}
                showDot={false}
                ariaLabel={t('online.challenge.increment')}
              />
            </div>
          )}
        </div>
      )}

      {/* Untimed games are always casual (server-enforced). The note sits
          right under the cadence, where the increment column would be, so
          the consequence reads next to the choice that caused it. */}
      {isUntimed && !hideRating && (
        <p className="challenge-custom-note">{t('online.challenge.noClockCasual')}</p>
      )}

      {/* Opening selection is meaningless for a composed position — hide it. */}
      {!position && (
        <div className="challenge-setting" ref={openingHintRef}>
          <label className="challenge-setting-label with-hint">
            {t('online.challenge.opening')}
            {/* The ballot entries are named by the F.I.D. strength codes,
                which no dropdown label can explain on its own. Variants
                with no federal table get their own text: there the draw is
                plain random moves, and claiming otherwise would be false. */}
            <button
              type="button"
              className="challenge-hint-btn"
              aria-label={t('online.challenge.openingInfoLabel')}
              aria-expanded={openingHintOpen}
              aria-controls={`${hintId}-opening`}
              onClick={() => setOpeningHintOpen((v) => !v)}
            >
              <HelpCircle size={18} aria-hidden="true" />
            </button>
          </label>
          {openingHintOpen && (
            <p className="challenge-hint-note" id={`${hintId}-opening`}>
              {t(hasBallot ? 'online.challenge.openingInfo' : 'online.challenge.openingInfoRandom')}
              {hasBallot && (
                /* Resta un `href` vero — accessibile, apribile col tasto destro, e
                   la stessa URL che il prerender pubblica. Il click però viene
                   intercettato quando chi ci ospita sa navigare: `target="_blank"`
                   in PWA installata butta fuori dall'app. Stesso schema delle
                   schede variante nella landing. */
                <a
                  className="challenge-hint-link"
                  href={buildOpeningsPath(i18n.language, variant)}
                  target={onOpenOpeningTables ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!onOpenOpeningTables) return;
                    e.preventDefault();
                    onOpenOpeningTables(variant);
                  }}
                >
                  {t('online.challenge.openingTablesLink')}
                </a>
              )}
            </p>
          )}
          <VariantSelect
            value={opening}
            onChange={(v) => { setOpening(v as OpeningMode); if (rememberSettings) setLastChallengeOpening(v as OpeningMode); }}
            variants={openingModes}
            label={openingLabel}
            dotColor={() => 'transparent'}
            renderLeading={(v) => openingLeading(v as OpeningMode)}
            ariaLabel={t('online.challenge.opening')}
          />
        </div>
      )}

      {/* Andata e ritorno con ogni apertura: due partite a colori invertiti sono il
          formato naturale di una sfida fra due persone, sorteggio o no. Col
          sorteggio è anche ciò che impedisce a uno dei due di tenersi il lato
          favorito, ed è per questo che lì è il predefinito. Non contro il
          computer: il ritorno non gli scambierebbe il colore. */}
      {!position && !vsComputer && (
        <div className="challenge-setting">
          <label className="challenge-setting-label">{t('online.challenge.legs')}</label>
          <VariantSelect
            value={String(legs)}
            onChange={(v) => { const n = (v === '1' ? 1 : 2) as MatchLegs; setLegsChoice(n); if (rememberSettings) setLastChallengeLegs(n); }}
            variants={LEGS_VALUES}
            label={legsLabel}
            dotColor={() => 'transparent'}
            renderLeading={legsLeading}
            ariaLabel={t('online.challenge.legs')}
          />
          {/* Solo andata above the single-game ceiling: the draw is never
              compensated, so the game is always casual. Rendered by the same
              condition that hides the rated/casual selector, so it never just
              disappears without saying why. Skipped when a note above already
              says "always casual". */}
          {drawnSingleLeg && !isUntimed && !hideRating && (
            <p className="challenge-custom-note">{t('online.challenge.openingDrawnSingleLegCasual')}</p>
          )}
        </div>
      )}

      {/* Rated/casual is offered for every mode — breakthrough and antidraughts can be
          rated and share the normal rating pool. Hidden for custom positions, for
          untimed games (both always casual; the ∞ note sits under the cadence row),
          for a drawn opening played once (the note under "Partite" explains it), for
          a guest host, who can only offer a friendly game — and per un avversario
          senza punteggio, dove al posto del comando c'è la nota in testa al modulo —
          sotto il nome di quell'avversario, che è di chi quella nota parla. */}
      {!hideRating && !isUntimed && !drawnSingleLeg && !opponentIsGuest && (
        opponent ? (
          /*
           * Avversario noto e iscritto: la domanda è BINARIA, e un interruttore è il
           * comando di una domanda binaria. L'etichetta È la parola — «Valutata» —
           * come la riga «Valutato» di Crea torneo, che è la stessa domanda sullo
           * stesso asse: una tendina con due voci sarebbe un clic in più per dire
           * la stessa cosa.
           *
           * Il guscio è un `<div>` e il testo una `<label htmlFor>`, con il cursore
           * a `wrapper="span"`: la combinazione documentata in `ToggleSwitch.tsx`,
           * che rende cliccabili sia il testo sia il cursore senza annidare due
           * etichette (HTML illecito).
           */
          <div className="challenge-setting challenge-toggle-row">
            <label className="challenge-toggle-testo" htmlFor={`${hintId}-valutata`}>
              {knownMatchTypeLabel(t, 'rated')}
            </label>
            <ToggleSwitch
              id={`${hintId}-valutata`}
              checked={resolvedMatchType === 'rated'}
              onChange={(acceso) => scegliTipo(acceso ? 'rated' : 'casual')}
              wrapper="span"
            />
          </div>
        ) : (
          <div className="challenge-setting">
            <label className="challenge-setting-label">{t('online.challenge.gameType')}</label>
            <VariantSelect
              value={resolvedMatchType}
              onChange={(v) => scegliTipo(v as MatchType)}
              variants={MATCH_TYPES}
              label={(v) => matchTypeLabel(t, v)}
              dotColor={() => 'transparent'}
              renderLeading={gameTypeLeading}
              ariaLabel={t('online.challenge.gameType')}
            />
            {/* "Solo valida" is the one type that turns players away, so it says so
                where the choice is made rather than at the door. Only here: in una
                Sfida diretta non c'è nessuno da mandare via — il destinatario è uno,
                lo si conosce, e un punteggio ce l'ha. */}
            {resolvedMatchType === 'rated' && (
              <p className="challenge-custom-note">{t('online.challenge.registeredOnlyNote')}</p>
            )}
          </div>
        )
      )}

      {/* Colour comes after the game-type row so rated/casual reads first.
          Assente in presenza: due umani sullo stesso schermo non scelgono "il
          proprio colore" — il Bianco muove per primo, e la damiera si gira. */}
      {!hotSeat && (
        <div className="challenge-setting">
          <label className="challenge-setting-label">{t('online.challenge.color')}</label>
          <VariantSelect
            value={color}
            onChange={(v) => { setColor(v as ChallengeColor); if (rememberSettings) setLastChallengeColor(v as ChallengeColor); }}
            variants={CHALLENGE_COLORS}
            label={colorLabelFor}
            dotColor={() => 'transparent'}
            renderLeading={colorLeading}
            ariaLabel={t('online.challenge.color')}
          />
        </div>
      )}

      <div className="challenge-setting" ref={modeHintRef}>
        {/* The hint sits next to the LABEL, so it reads as "what does
            Modalità mean?" rather than as a control beside the dropdown.
            Il bottone è piccolo quanto il suo glifo e si porta i 44px tattili in un
            ::after trasparente (vedi .challenge-hint-btn): prima era una scatola da 44px
            attorno a un'icona da 18, cioè 13px di vuoto che lo staccavano dall'etichetta
            e una macchia grigia grande il triplo del segno al tocco. */}
        <label className="challenge-setting-label with-hint">
          {t('online.challenge.mode')}
          {/* Breakthrough / antidraughts invert or short-circuit the win
              condition, which no label can convey. Click-toggled rather
              than a hover tooltip: hover does not exist on touch. */}
          <button
            type="button"
            className="challenge-hint-btn"
            aria-label={t('online.challenge.modeInfoLabel')}
            aria-expanded={modeHintOpen}
            aria-controls={`${hintId}-mode`}
            onClick={() => setModeHintOpen((v) => !v)}
          >
            <HelpCircle size={18} aria-hidden="true" />
          </button>
        </label>
        {modeHintOpen && (
          <p className="challenge-hint-note" id={`${hintId}-mode`}>
            <strong>{t('online.challenge.modeBreakthrough')}</strong>
            {t('online.challenge.modeBreakthroughInfo')}
            <strong>{t('online.challenge.modeAntidraughts')}</strong>
            {t('online.challenge.modeAntidraughtsInfo')}
            <strong>{t('online.challenge.modeBlind')}</strong>
            {t('online.challenge.modeBlindInfo')}
          </p>
        )}
        <VariantSelect
          value={gameMode}
          onChange={(v) => { setGameMode(v as GameMode); if (rememberSettings) setLastChallengeGameMode(v as GameMode); }}
          variants={GAME_MODES}
          label={gameModeLabelFor}
          dotColor={() => 'transparent'}
          renderLeading={gameModeLeading}
          ariaLabel={t('online.challenge.mode')}
        />
      </div>
    </div>
  );
}
